import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { PremiumPaywall } from '@/components/PremiumPaywall';
import { Analytics } from '@/lib/analytics';

type PaywallOptions = {
  title?: string;
  description?: string;
  /** Funnel attribution for Firebase (ai_run_coach, chat_weekly_limit, …). */
  source?: string;
};

type PremiumGateContextValue = {
  openPaywall: (options?: PaywallOptions) => void;
  closePaywall: () => void;
};

const PremiumGateContext = createContext<PremiumGateContextValue | null>(null);

export function PremiumGateProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState<string | undefined>();
  const [description, setDescription] = useState<string | undefined>();
  const [source, setSource] = useState<string>('unknown');

  const openPaywall = useCallback((options?: PaywallOptions) => {
    const src = options?.source || 'unknown';
    setTitle(options?.title);
    setDescription(options?.description);
    setSource(src);
    Analytics.premiumGateHit(src);
    setOpen(true);
  }, []);

  const closePaywall = useCallback(() => setOpen(false), []);

  const value = useMemo(
    () => ({ openPaywall, closePaywall }),
    [openPaywall, closePaywall],
  );

  return (
    <PremiumGateContext.Provider value={value}>
      {children}
      <PremiumPaywall
        open={open}
        onOpenChange={setOpen}
        title={title}
        description={description}
        source={source}
      />
    </PremiumGateContext.Provider>
  );
}

export function usePremiumGate() {
  const ctx = useContext(PremiumGateContext);
  if (!ctx) {
    return {
      openPaywall: (_options?: PaywallOptions) => {
        console.warn('[usePremiumGate] called outside PremiumGateProvider');
      },
      closePaywall: () => {},
    };
  }
  return ctx;
}
